import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UserStorageService } from 'services'

import { CertificationsType } from 'types/IndustryCertifications.types'

import { TableNotFoundHint } from 'components/TableNotFoundHint'

import { Icon } from 'components/Icon'
import { Modal } from 'components/Modal'
import { Button } from 'components/Button'
import { StatusBox } from 'components/StatusBox'

import * as S from './styled'
import { format, parseISO } from 'date-fns'

export type CertificationTableProps = {
  rows: CertificationsType[]
}

export function IndustryCertificateDetailsTable({
  rows
}: CertificationTableProps) {
  const { t } = useTranslation()

  const { user } = UserStorageService.getUserData()

  const [file, setFile] = useState(null)
  const [openModalCertification, setOpenModalCertification] = useState(false)

  function openPDF(file) {
    if (!file) return
    setOpenModalCertification(!openModalCertification)
    setFile(file)
  }

  const handleDownload = file => {
    try {
      const link = document.createElement('a')
      link.href = file.src
      link.download = file.filename || 'downloaded-file'
      document.body.appendChild(link)

      window.open(link.href, '_blank')

      document.body.removeChild(link)

      if (file.src.startsWith('blob:')) {
        window.URL.revokeObjectURL(file.src)
      }
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error)
    }
  }

  return (
    <S.Wrapper data-testid="IndustryTable">
      {openModalCertification && (
        <Modal
          content={
            <>
              <S.ContentPdf>
                <iframe
                  className="pdf-viewer"
                  src={`${file.src}#toolbar=0&navpanes=0&scrollbar=0`}
                  allowTransparency={true}
                />
              </S.ContentPdf>
              <Button variant="primary" onClick={() => handleDownload(file)}>
                <p>{t('indCertifications:downloadCertification')}</p>
              </Button>
            </>
          }
          cancelButton={false}
          onExit={() => setOpenModalCertification(!openModalCertification)}
        />
      )}

      <S.Teste>
        <S.ScrollWrapper>
          <S.Table id="table-fixed">
            <tbody>
              <S.Line id="header">
                <S.Header>{t('indCertifications:certificationName')}</S.Header>
                <S.Header>{t('indCertifications:acronymous')}</S.Header>
                <S.Header>
                  {t('indCertifications:certificationNumber')}
                </S.Header>
                <S.Header>{t('indCertifications:emissionDate')}</S.Header>
                <S.Header>{t('indCertifications:expirationDate')}</S.Header>
                <S.Header>{t('indCertifications:certifying')}</S.Header>
                {user.tag === 'admin' && (
                  <S.Header>{t('indCertifications:status')}</S.Header>
                )}
                <S.Header>{t('indCertifications:certificateLogo')}</S.Header>
              </S.Line>
              {rows &&
                rows.map(each => (
                  <S.Line id="values" key={each.id}>
                    <S.Column>{each.name}</S.Column>
                    <S.Column>{each.acronym}</S.Column>
                    <S.Column>{each.number}</S.Column>
                    <S.Column>
                      {format(parseISO(each.dtEmitted), 'dd/MM/yyyy')}
                    </S.Column>
                    <S.Column>
                      {format(parseISO(each.dtExpiry), 'dd/MM/yyyy')}
                    </S.Column>
                    <S.Column>{each.nameCertifier}</S.Column>
                    {user.tag === 'admin' && (
                      <S.Column className="center">
                        <StatusBox
                          variant={each.status ? 'active' : 'inactive'}
                        >
                          {each.status
                            ? t('indCertifications:active')
                            : t('indCertifications:inactive')}
                        </StatusBox>
                      </S.Column>
                    )}
                    <S.Column className="center">
                      <S.LogomarcaImage
                        src={each.logo ? each.logo.src : '/images/logo_jbs.png'}
                      />
                    </S.Column>
                  </S.Line>
                ))}
            </tbody>
          </S.Table>
        </S.ScrollWrapper>
        <S.Table id="table-fixed">
          <tbody>
            <S.Line id="header">
              <S.Header>{'Visualizar'}</S.Header>
            </S.Line>
            {Boolean(rows && rows.length) &&
              rows.map((row, index) => (
                <S.Line key={index}>
                  <S.Column id="actions-column">
                    <S.Link onClick={() => openPDF(row.file)}>
                      <Icon icon="input-visible" height={20} width={20} />
                    </S.Link>
                  </S.Column>
                </S.Line>
              ))}
          </tbody>
        </S.Table>
      </S.Teste>
      {rows && !rows.length && <TableNotFoundHint />}
    </S.Wrapper>
  )
}
